import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Settings, 
  Palette, 
  Globe, 
  Clock,
  Save,
  ArrowLeft,
  Monitor,
  Moon,
  Sun,
  Languages,
  Volume2
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/contexts/AuthContext'

interface SystemSettings {
  theme: 'light' | 'dark' | 'auto'
  language: 'en' | 'es' | 'fr' | 'de'
  timezone: string
  dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD'
  timeFormat: '12h' | '24h'
  fontSize: 'small' | 'medium' | 'large'
  animations: boolean
  soundEffects: boolean
  autoSave: boolean
  notifications: boolean
}

export function SystemSettingsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isSaving, setIsSaving] = useState(false)
  
  const [settings, setSettings] = useState<SystemSettings>({
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    fontSize: 'medium',
    animations: true,
    soundEffects: true,
    autoSave: true,
    notifications: true
  })

  const handleSettingChange = (key: keyof SystemSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      // In a real app, save settings to backend
    } catch (error) {
      console.error('Save error:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const themes = [
    { value: 'light', label: 'Light', icon: Sun, description: 'Clean and bright interface' },
    { value: 'dark', label: 'Dark', icon: Moon, description: 'Easy on the eyes in low light' },
    { value: 'auto', label: 'Auto', icon: Monitor, description: 'Follows system preference' }
  ]

  const languages = [
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'es', label: 'Español', flag: '🇪🇸' },
    { value: 'fr', label: 'Français', flag: '🇫🇷' },
    { value: 'de', label: 'Deutsch', flag: '🇩🇪' }
  ]

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney'
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/more')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
            <p className="text-gray-600">General system preferences and configurations</p>
          </div>
        </div>
        <Button 
          onClick={handleSave}
          disabled={isSaving}
          className="bg-primary-600 hover:bg-primary-700"
        >
          {isSaving ? (
            <>
              <Save className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Appearance */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Appearance</h3>
          
          {/* Theme Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Theme</label>
            <div className="grid grid-cols-3 gap-3">
              {themes.map((theme) => (
                <button
                  key={theme.value}
                  onClick={() => handleSettingChange('theme', theme.value)}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    settings.theme === theme.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <theme.icon className="h-5 w-5 mb-2 text-gray-600" />
                  <div className="font-medium text-sm">{theme.label}</div>
                  <div className="text-xs text-gray-500">{theme.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Font Size */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Font Size</label>
            <div className="flex space-x-2">
              {[
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' }
              ].map((size) => (
                <button
                  key={size.value}
                  onClick={() => handleSettingChange('fontSize', size.value)}
                  className={`px-3 py-2 text-sm border rounded-md transition-colors ${
                    settings.fontSize === size.value
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {size.label}
                </button>
              ))}
            </div>
          </div>

          {/* Animations */}
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">Animations</h4>
              <p className="text-sm text-gray-600">Enable smooth transitions and animations</p>
            </div>
            <button
              onClick={() => handleSettingChange('animations', !settings.animations)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.animations ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.animations ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </Card>

        {/* Language & Region */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Language & Region</h3>
          
          {/* Language Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Language</label>
            <div className="space-y-2">
              {languages.map((lang) => (
                <label key={lang.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="language"
                    value={lang.value}
                    checked={settings.language === lang.value}
                    onChange={() => handleSettingChange('language', lang.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-lg">{lang.flag}</span>
                  <span className="font-medium">{lang.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Timezone */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => handleSettingChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {timezones.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>

          {/* Date Format */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Date Format</label>
            <div className="space-y-2">
              {[
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '12/25/2024' },
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '25/12/2024' },
                { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2024-12-25' }
              ].map((format) => (
                <label key={format.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="dateFormat"
                    value={format.value}
                    checked={settings.dateFormat === format.value}
                    onChange={() => handleSettingChange('dateFormat', format.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium">{format.label}</div>
                    <div className="text-sm text-gray-600">{format.example}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Time Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Time Format</label>
            <div className="space-y-2">
              {[
                { value: '12h', label: '12-hour (AM/PM)', example: '2:30 PM' },
                { value: '24h', label: '24-hour', example: '14:30' }
              ].map((format) => (
                <label key={format.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="timeFormat"
                    value={format.value}
                    checked={settings.timeFormat === format.value}
                    onChange={() => handleSettingChange('timeFormat', format.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium">{format.label}</div>
                    <div className="text-sm text-gray-600">{format.example}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </Card>

        {/* Behavior */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Behavior</h3>
          
          <div className="space-y-4">
            {[
              {
                key: 'soundEffects',
                label: 'Sound Effects',
                description: 'Play sounds for notifications and actions',
                icon: Volume2
              },
              {
                key: 'autoSave',
                label: 'Auto Save',
                description: 'Automatically save your work as you type',
                icon: Save
              },
              {
                key: 'notifications',
                label: 'Notifications',
                description: 'Show system notifications and alerts',
                icon: Settings
              }
            ].map((setting) => (
              <div key={setting.key} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <setting.icon className="h-5 w-5 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">{setting.label}</h4>
                    <p className="text-sm text-gray-600">{setting.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleSettingChange(setting.key as keyof SystemSettings, !settings[setting.key as keyof SystemSettings])}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings[setting.key as keyof SystemSettings] ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings[setting.key as keyof SystemSettings] ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>
        </Card>

        {/* Current Settings Summary */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Settings</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Theme:</span>
              <Badge variant="outline">{settings.theme}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Language:</span>
              <Badge variant="outline">{settings.language}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Timezone:</span>
              <Badge variant="outline">{settings.timezone}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Date Format:</span>
              <Badge variant="outline">{settings.dateFormat}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Time Format:</span>
              <Badge variant="outline">{settings.timeFormat}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Font Size:</span>
              <Badge variant="outline">{settings.fontSize}</Badge>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}
